import { createBrowserRouter } from 'react-router';

import { ProtectedRoute } from '@/components/auth/protected-route';
// Module landing page component for placeholder modules
import { ModuleLandingPage } from '@/components/module/module-landing-page';
import { commercialModule, connectivityModule, productionModule } from '@/config/modules';
import { GuestLayout } from '@/layouts/guest-layout';
import { MainLayout } from '@/layouts/main-layout';
// Admin pages
import { AdminLandingPage } from '@/pages/admin';
import { ControlsPage } from '@/pages/admin/controls';
import { ProfileListPage } from '@/pages/admin/profiles';
import { UserProfilePage } from '@/pages/admin/user';
import { UserCreatePage, UserEditPage, UserListPage } from '@/pages/admin/users';
import { ForgotPasswordPage } from '@/pages/auth/forgot-password';
// Auth pages
import { LoginPage } from '@/pages/auth/login';
import { ResetPasswordPage } from '@/pages/auth/reset-password';
import { SetPasswordPage } from '@/pages/auth/set-password';
// Commercial pages
import { AccountCreatePage } from '@/pages/commercial/accounts/create';
import { AccountDetailsPage } from '@/pages/commercial/accounts/details';
import { AccountListPage } from '@/pages/commercial/accounts/list';
// External pages
import { ExternalDocumentUploadPage } from '@/pages/commercial/credit-analysis/external-form';
import { CreditAnalysisListPage } from '@/pages/commercial/credit-analysis/list';
import { CreditAnalysisUpdatePage } from '@/pages/commercial/credit-analysis/update';
import {
  PriceTableClonePage,
  PriceTableCreatePage,
  PriceTableEditPage,
  PriceTableListPage,
} from '@/pages/commercial/price-table';
import { ProductListPage } from '@/pages/commercial/products';
import { ProposalCreatePage } from '@/pages/commercial/proposals/create';
import { ProposalListPage } from '@/pages/commercial/proposals/list';
import { ProposalUpdatePage } from '@/pages/commercial/proposals/update';
// Connectivity pages
import {
  BatchLoraActivationPage,
  KoraSyncPage,
  LoraActivationLogsPage,
  LoraActivationPage,
} from '@/pages/connectivity';
// Main pages
import { ErrorPage } from '@/pages/error';
import { HomePage } from '@/pages/home';
import { NotFoundPage } from '@/pages/not-found';
// Production pages
import {
  ConfigProfileCreatePage,
  ConfigProfileEditPage,
  ConfigProfileListPage,
  ConfigProfileUpdateFromOrderPage,
} from '@/pages/production/config-profiles';
import { DeviceCreatePage, DeviceEditPage, DeviceListPage } from '@/pages/production/devices';
import { ProductionLogsPage } from '@/pages/production/logs';
import { AutoTestLogDetailPage } from '@/pages/production/logs/auto-test/detail';
import { AutoTestLogListPage } from '@/pages/production/logs/auto-test/list';
import { ConfigurationLogDetailPage } from '@/pages/production/logs/configurator/detail';
import { ConfigurationLogListPage } from '@/pages/production/logs/configurator/list';
import { FirmwareUpdateLogDetailPage } from '@/pages/production/logs/firmware-update/detail';
import { FirmwareUpdateLogListPage } from '@/pages/production/logs/firmware-update/list';
import { IdentificationLogDetailPage } from '@/pages/production/logs/identificator/detail';
import { IdentificationLogListPage } from '@/pages/production/logs/identificator/list';
import {
  AutoTestPage,
  CheckConfigurationPage,
  CommandsPage,
  ConfiguratorPage,
  FirmwareUpdatePage,
  IdentificatorPage,
  ProductionToolsPage,
} from '@/pages/production/tools';

// Placeholder pages for modules (to be implemented)
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      <p className="mt-2 text-muted-foreground">Em desenvolvimento...</p>
    </div>
  );
}

export const router = createBrowserRouter([
  // Redirect root to home
  {
    path: '/',
    lazy: async () => {
      const { Navigate } = await import('react-router');
      return { Component: () => <Navigate to="/home" replace /> };
    },
  },

  // Guest routes (login, forgot password, etc.)
  {
    element: <GuestLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
      },
    ],
  },

  // Public routes (no layout, external access)
  {
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/external-link/:linkId/documents',
        element: <ExternalDocumentUploadPage />,
      },
    ],
  },

  // Protected routes - set-password (fullscreen, no sidebar)
  {
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/set-password',
        element: <SetPasswordPage />,
      },
    ],
  },

  // Protected routes - all use the same MainLayout with unified sidebar
  {
    element: <ProtectedRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <MainLayout />,
        errorElement: <ErrorPage />,
        children: [
          // Home
          {
            path: '/home',
            element: <HomePage />,
          },

          // Admin module
          {
            path: '/admin',
            element: <AdminLandingPage />,
          },
          {
            path: '/admin/users',
            element: <UserListPage />,
          },
          {
            path: '/admin/users/create',
            element: <UserCreatePage />,
          },
          {
            path: '/admin/users/:id/edit',
            element: <UserEditPage />,
          },
          {
            path: '/admin/users/:id',
            element: <PlaceholderPage title="Detalhes do Usuário" />,
          },
          {
            path: '/admin/user/profile',
            element: <UserProfilePage />,
          },
          {
            path: '/admin/profiles',
            element: <ProfileListPage />,
          },
          {
            path: '/admin/profiles/:id',
            element: <PlaceholderPage title="Editar Permissões" />,
          },
          {
            path: '/admin/control',
            element: <ControlsPage />,
          },

          // Commercial module
          {
            path: '/commercial',
            element: (
              <ModuleLandingPage
                title={commercialModule.title}
                description={commercialModule.description}
                actions={commercialModule.landingActions}
              />
            ),
          },
          {
            path: '/commercial/products',
            element: <ProductListPage />,
          },
          // Next.js compatible route
          {
            path: '/commercial/product/management',
            element: <ProductListPage />,
          },
          {
            path: '/commercial/product/category',
            lazy: async () => {
              const { Navigate } = await import('react-router');
              return {
                Component: () => <Navigate to="/commercial/products?tab=categories" replace />,
              };
            },
          },
          {
            path: '/commercial/accounts',
            element: <AccountListPage />,
          },
          {
            path: '/commercial/accounts/create',
            element: <AccountCreatePage />,
          },
          {
            path: '/commercial/accounts/:id',
            element: <AccountDetailsPage />,
          },
          {
            path: '/commercial/proposal',
            element: <ProposalListPage />,
          },
          {
            path: '/commercial/proposal/form/create',
            element: <ProposalCreatePage />,
          },
          {
            path: '/commercial/proposal/form/update/:id',
            element: <ProposalUpdatePage />,
          },
          // Vite route alias (plural to singular redirect)
          {
            path: '/commercial/proposals',
            lazy: async () => {
              const { Navigate } = await import('react-router');
              return {
                Component: () => <Navigate to="/commercial/proposal" replace />,
              };
            },
          },
          {
            path: '/commercial/proposals',
            element: <ProposalListPage />,
          },
          {
            path: '/commercial/price-tables',
            element: <PriceTableListPage />,
          },
          // Next.js compatible route
          {
            path: '/commercial/price-table',
            element: <PriceTableListPage />,
          },
          {
            path: '/commercial/price-table/form/create',
            element: <PriceTableCreatePage />,
          },
          {
            path: '/commercial/price-table/form/edit/:id',
            element: <PriceTableEditPage />,
          },
          {
            path: '/commercial/price-table/form/clone/:id',
            element: <PriceTableClonePage />,
          },
          {
            path: '/commercial/credit-analysis',
            element: <CreditAnalysisListPage />,
          },
          {
            path: '/commercial/credit-analysis/:id',
            element: <CreditAnalysisUpdatePage />,
          },

          // Production module
          {
            path: '/production',
            element: (
              <ModuleLandingPage
                title={productionModule.title}
                description={productionModule.description}
                actions={productionModule.landingActions}
              />
            ),
          },
          {
            path: '/production/devices',
            element: <DeviceListPage />,
          },
          {
            path: '/production/devices/create',
            element: <DeviceCreatePage />,
          },
          {
            path: '/production/devices/edit',
            element: <DeviceEditPage />,
          },
          {
            path: '/production/config-profiles',
            element: <ConfigProfileListPage />,
          },
          {
            path: '/production/config-profiles/create',
            element: <ConfigProfileCreatePage />,
          },
          {
            path: '/production/config-profiles/edit',
            element: <ConfigProfileEditPage />,
          },
          {
            path: '/production/config-profiles/update-from-order',
            element: <ConfigProfileUpdateFromOrderPage />,
          },
          {
            path: '/production/logs',
            element: <ProductionLogsPage />,
          },
          {
            path: '/production/logs/auto-test',
            element: <AutoTestLogListPage />,
          },
          {
            path: '/production/logs/auto-test/:id',
            element: <AutoTestLogDetailPage />,
          },
          {
            path: '/production/logs/configurator',
            element: <ConfigurationLogListPage />,
          },
          {
            path: '/production/logs/configurator/:id',
            element: <ConfigurationLogDetailPage />,
          },
          {
            path: '/production/logs/firmware-update',
            element: <FirmwareUpdateLogListPage />,
          },
          {
            path: '/production/logs/firmware-update/:id',
            element: <FirmwareUpdateLogDetailPage />,
          },
          {
            path: '/production/logs/identificator',
            element: <IdentificationLogListPage />,
          },
          {
            path: '/production/logs/identificator/:id',
            element: <IdentificationLogDetailPage />,
          },
          {
            path: '/production/tools',
            element: <ProductionToolsPage />,
          },
          {
            path: '/production/tools/auto-test',
            element: <AutoTestPage />,
          },
          {
            path: '/production/tools/configurator',
            element: <ConfiguratorPage />,
          },
          {
            path: '/production/tools/firmware-update',
            element: <FirmwareUpdatePage />,
          },
          {
            path: '/production/tools/identificator',
            element: <IdentificatorPage />,
          },
          {
            path: '/production/tools/check-configuration',
            element: <CheckConfigurationPage />,
          },
          {
            path: '/production/tools/commands',
            element: <CommandsPage />,
          },
          // Next.js compatible routes
          {
            path: '/production/configuration-profile/management',
            element: <ConfigProfileListPage />,
          },
          {
            path: '/production/configuration-profile/form/create',
            element: <ConfigProfileCreatePage />,
          },
          {
            path: '/production/configuration-profile/form/update',
            element: <ConfigProfileEditPage />,
          },
          {
            path: '/production/configuration-profile/form/update-from-production-order',
            element: <ConfigProfileUpdateFromOrderPage />,
          },

          // Connectivity module
          {
            path: '/connectivity',
            element: (
              <ModuleLandingPage
                title={connectivityModule.title}
                description={connectivityModule.description}
                actions={connectivityModule.landingActions}
              />
            ),
          },
          {
            path: '/connectivity/kora-sync',
            element: <KoraSyncPage />,
          },
          {
            path: '/connectivity/lora-activation',
            element: <LoraActivationPage />,
          },
          {
            path: '/connectivity/batch-lora-activation',
            element: <BatchLoraActivationPage />,
          },
          {
            path: '/connectivity/lora-activation-logs',
            element: <LoraActivationLogsPage />,
          },
        ],
      },
    ],
  },

  // 404 page
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
